import { api, apiCall, apiPrefix } from "@services/API/Requests";

import { MapTenantDepartment } from "@services/API/MapData";
import { handleResponse } from "@services/API/StatusCodes";
import { store } from "@services/StateManagement/Store";

export const getTenantDepartmentData = async (
  setTenantDepartment: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  tenantDepartmentId: string,
) => {
  if (!tenantDepartmentId) return;

  return apiCall({
    method: "get",
    url: `${apiPrefix}/tenant_departments/${tenantDepartmentId}`,
    setLoading,
    onSuccess: (response) => {
      const tenantDepartment = MapTenantDepartment(response.data);

      setTenantDepartment(tenantDepartment);
      const responseMessage = handleResponse(response, "部署");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const getTenantDepartmentsData = async (
  setTenantDepartment,
  setOriginalTenantDepartments,
  setLoading,
  setError,
  setTotalTenantDepartments,
  currentPage,
  itemsPerPage,
) => {
  return apiCall({
    method: "get",
    url: `v1/tenant_departments?current_page=${currentPage}&per_page=${itemsPerPage}`,
    setLoading,
    onSuccess: (response) => {
      const departments =
        response.data.tenant_departments.map(MapTenantDepartment);

      setTenantDepartment(departments);
      setTotalTenantDepartments(response.data.pagination.total_count);
      setOriginalTenantDepartments(departments);
      const responseMessage = handleResponse(response, "部署");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const putTenantDepartmentsData = async (
  tenantDepartmentData: any,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (!tenantDepartmentData) return;

  return apiCall({
    method: "put",
    url: `${apiPrefix}/tenant_departments/${tenantDepartmentData.id}`,
    data: tenantDepartmentData,
    setLoading,
    onSuccess: (response) => {
      const responseMessage = handleResponse(response, "部署");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

// this functions slightly different to the others - we update this as soon as we alter it so the user can see the change.
export const postTenantDepartmentData = async (
  setTenantDepartmentData: (data: any) => void,
  tenantDepartmentData: any,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (!tenantDepartmentData) return;

  const { id, uid, ...filteredData } = tenantDepartmentData;

  return apiCall({
    method: "post",
    url: `${apiPrefix}/tenant_departments/`,
    data: filteredData,
    setLoading,
    onSuccess: (response) => {
      const tenantDepartment = {
        id: response.data.id,
        tenant_id: response.data.tenant_id,
        name: response.data.name,
        parent: response.data.parent ? response.data.parent.id : null,
        name_kana: response.data.name_kana,
        code: response.data.code,
        display_order: response.data.display_order,
        department_level: response.data.department_level,
      };

      setTenantDepartmentData(tenantDepartment);
      const responseMessage = handleResponse(response, "部署");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const deleteTenantDepartmentData = async (
  tenantDepartmentId: string,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (!tenantDepartmentId) return;

  return apiCall({
    method: "delete",
    url: `${apiPrefix}/tenant_departments/${tenantDepartmentId}`,
    setLoading,
    onSuccess: (response) => {
      const responseMessage = handleResponse(response, "部署");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const getTenantDepartmentCsv = async (
  setLoading: () => void,
  setError: (error: any) => void,
  deptIds: number[],
) => {
  const url = `${apiPrefix}/tenant_departments/export_csv`;

  const body = {
    tenant_department_ids: deptIds,
  };

  return apiCall({
    method: "post",
    url: url,
    data: body,
    setLoading,
    onSuccess: (response: any) => {
      const csvData = response.data;
      return csvData;
    },
    onError: (errorResponse: any) => {
      setError(errorResponse);
    },
  });
};

export const postTenantDepartmentArray = async (
  tenantDepartmentDataArray: any[],
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (tenantDepartmentDataArray) {
    let response;
    let responseMessage;
    let failedTenantDepartments;
    let createdTenantDepartments;
    let request_id;

    const tenantDepartmentArray = tenantDepartmentDataArray.map(
      (department) => {
        const { ...filteredTenantDepartmentData } = department;

        return {
          ...filteredTenantDepartmentData,
        };
      },
    );

    try {
      const header = store.getState().api.headers;
      api.defaults.headers = header;

      setLoading(true);
      response = await api.post(
        `${apiPrefix}/tenant_departments/create_with_csv`,
        {
          tenant_department: tenantDepartmentArray,
        },
      );

      responseMessage = handleResponse(response, "部署");
      createdTenantDepartments = response?.data?.created_departments;
      failedTenantDepartments = response?.data?.failed_departments;
      request_id = response?.data?.request_id;
    } catch (err) {
      console.error(err.message);
      failedTenantDepartments = err.response.data.failed_users;
      const errorResponse =
        err.response.data.errors || err.response.data.error || err.message;
      responseMessage = { status: err.status, message: errorResponse };
      setError(errorResponse);
    } finally {
      setLoading(false);

      // if (failedTenantDepartments != null) {
      //   if (failedTenantDepartments.length > 1) {
      //     failedTenantDepartments.forEach((department) => {
      //       const translatedMessage = TranslateErrorMessage(department.error[0]);
      //       department.error = translatedMessage;
      //     });
      //   } else {
      //     const translatedMessage = TranslateErrorMessage(failedTenantDepartments.error[0]);
      //     failedTenantDepartments.error = translatedMessage;
      //   }
      // }

      return {
        responseMessage,
        failed_departments: failedTenantDepartments,
        created_departments: createdTenantDepartments,
        request_id: request_id,
      };
    }
  }
};
